export interface SeasonOption {
  label: string
  start: string // YYYY-MM-DD
  end: string   // YYYY-MM-DD
}

/** Monday of the week containing `date`. */
export function getWeekStart(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay() // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day // shift to Monday
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Sunday of the week containing `date`. */
export function getWeekEnd(date: Date): Date {
  const start = getWeekStart(date)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  end.setHours(23, 59, 59, 999)
  return end
}

/** Shift a date forward/backward by `weeks` weeks. */
export function shiftWeeks(date: Date, weeks: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + weeks * 7)
  return d
}

/** Format as YYYY-MM-DD for API params. */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Display a week range, e.g. "Feb 23 - Mar 1, 2025". */
export function formatWeekRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  const startStr = start.toLocaleDateString('en-US', opts)
  const endStr = end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })
  return `${startStr} - ${endStr}`
}

/**
 * Academic-year seasons: Aug 1 → Jul 31.
 * Returns the most recent `count` seasons, newest first.
 */
export function getSeasonOptions(count = 5): SeasonOption[] {
  const now = new Date()
  const currentStartYear = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1

  return Array.from({ length: count }, (_, i) => {
    const y = currentStartYear - i
    return {
      label: `${y}-${String(y + 1).slice(-2)}`,
      start: `${y}-08-01`,
      end: `${y + 1}-07-31`,
    }
  })
}

/** The season containing today. */
export function getCurrentSeason(): SeasonOption {
  return getSeasonOptions(1)[0]
}

/** True if `weekStart` falls within the current calendar week. */
export function isCurrentWeek(weekStart: Date): boolean {
  const now = getWeekStart(new Date())
  return weekStart.getTime() === now.getTime()
}

// The scoreboard buckets days in US Eastern (mirrors the backend
// ScoreboardController::SCOREBOARD_ZONE) so "Today"/"Yesterday" labels line up
// with the server's day boundaries regardless of the viewer's timezone.
const SCOREBOARD_ZONE = 'America/New_York'

/** YYYY-MM-DD for `date` in the scoreboard zone. */
export function scoreboardDateString(date: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone: SCOREBOARD_ZONE }).format(date)
}

/** Parse a YYYY-MM-DD scoreboard date as a plain local calendar date (no TZ shift). */
function parseScoreboardDate(isoDate: string): Date {
  const [y, m, d] = isoDate.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * Human day-header label for a scoreboard day.
 * "Today" / "Yesterday" for the two most recent days, else "Saturday, September 6".
 */
export function formatDayHeader(isoDate: string): string {
  const today = scoreboardDateString()
  const yesterday = scoreboardDateString(new Date(Date.now() - 24 * 60 * 60 * 1000))
  if (isoDate === today) return 'Today'
  if (isoDate === yesterday) return 'Yesterday'
  return parseScoreboardDate(isoDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/** Compact label for a date chip: "Today" / "Yest" / short weekday ("Sat"). */
export function formatDayChip(isoDate: string): string {
  const today = scoreboardDateString()
  const yesterday = scoreboardDateString(new Date(Date.now() - 24 * 60 * 60 * 1000))
  if (isoDate === today) return 'Today'
  if (isoDate === yesterday) return 'Yest'
  return parseScoreboardDate(isoDate).toLocaleDateString('en-US', { weekday: 'short' })
}

/** The last `count` scoreboard days (newest first) as YYYY-MM-DD strings. */
export function recentScoreboardDays(count: number): string[] {
  return Array.from({ length: count }, (_, i) =>
    scoreboardDateString(new Date(Date.now() - i * 24 * 60 * 60 * 1000)),
  )
}
